import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Building2, Globe, Instagram, Archive, ArchiveRestore } from "lucide-react";

export default function ClientsList() {
    const [clients, setClients] = useState([]);
    const [q, setQ] = useState("");
    const [showArchived, setShowArchived] = useState(false);

    const load = useCallback(() => {
        const url = showArchived ? "/clients?include_archived=true" : "/clients";
        api.get(url).then(({ data }) => setClients(data));
    }, [showArchived]);

    useEffect(() => { load(); }, [load]);

    const filtered = clients.filter(c => c.company_name?.toLowerCase().includes(q.toLowerCase()));

    return (
        <div className="p-6 sm:p-8 lg:p-10 max-w-7xl mx-auto" data-testid="clients-page">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
                <div>
                    <div className="label-micro mb-2">Database</div>
                    <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight">Clients</h1>
                </div>
                <div className="flex items-center gap-3">
                    <label
                        className="flex items-center gap-2 text-xs font-mono text-zinc-400 cursor-pointer select-none"
                        data-testid="clients-show-archived-toggle"
                    >
                        <input
                            type="checkbox"
                            checked={showArchived}
                            onChange={(e) => setShowArchived(e.target.checked)}
                            className="accent-amber-500"
                        />
                        Show archived
                    </label>
                    <Link to="/clients/new" data-testid="add-client-btn" className="btn-primary">
                        <Plus className="w-4 h-4" /> Add client
                    </Link>
                </div>
            </div>

            <div className="mb-6">
                <input
                    data-testid="client-search"
                    placeholder="Search by company name…"
                    value={q} onChange={e => setQ(e.target.value)}
                    className="w-full max-w-md bg-black border border-zinc-800 rounded-sm px-3 py-2.5 focus:border-zinc-500 outline-none"
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filtered.map(c => (
                    <ClientCard key={c.id} c={c} onChanged={load} />
                ))}
                {filtered.length === 0 && (
                    <div className="col-span-full text-sm text-zinc-500 text-center py-16 border border-dashed border-zinc-900 rounded-sm">
                        {showArchived ? "No archived clients." : "No clients found."}
                    </div>
                )}
            </div>
        </div>
    );
}

/** Client card with quick archive / unarchive.
 *  Archive flow first fetches /clients/{id}/archive-impact so the user sees
 *  exactly how many active projects they're about to cascade-archive. */
function ClientCard({ c, onChanged }) {
    const [busy, setBusy] = useState(false);

    const onAction = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (busy) return;

        if (c.is_archived) {
            // Unarchive — uses /clients/{id}/unarchive (Phase N.6).
            setBusy(true);
            try {
                await api.post(`/clients/${c.id}/unarchive`);
                toast.success("Client unarchived.");
                await onChanged();
            } catch (err) {
                toast.error(err.userMessage || err.message || "Could not unarchive");
            } finally { setBusy(false); }
            return;
        }

        setBusy(true);
        let impact;
        try {
            const r = await api.get(`/clients/${c.id}/archive-impact`);
            impact = r.data;
        } catch (err) {
            toast.error(err.userMessage || err.message || "Could not load impact");
            setBusy(false);
            return;
        }

        const active = impact.active_projects || 0;
        let archiveProjects = false;
        if (active > 0) {
            const proceed = window.confirm(
                `This client has ${active} active project${active === 1 ? "" : "s"}.\n\n` +
                `Click OK to archive the client AND all ${active} project${active === 1 ? "" : "s"} (recommended).\n` +
                `Click Cancel to abort.\n\n` +
                `Outputs, versions and approvals are always preserved. You can unarchive any time.`
            );
            if (!proceed) { setBusy(false); return; }
            archiveProjects = true;
        } else {
            const proceed = window.confirm(
                `Archive "${c.company_name}"? It will be hidden from the active client list. ` +
                `No projects exist under it. You can unarchive any time.`
            );
            if (!proceed) { setBusy(false); return; }
        }

        try {
            await api.post(`/clients/${c.id}/archive${archiveProjects ? "?archive_projects=true" : ""}`);
            toast.success(
                archiveProjects
                    ? `Client and ${active} project${active === 1 ? "" : "s"} archived.`
                    : "Client archived."
            );
            await onChanged();
        } catch (err) {
            toast.error(err.userMessage || err.message || "Could not archive");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Link
            to={`/clients/${c.id}`}
            data-testid={`client-card-${c.id}`}
            className={`card-base card-hover p-6 group relative block ${c.is_archived ? "opacity-70" : ""}`}
        >
            <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-zinc-900 border border-zinc-800 grid place-items-center rounded-sm">
                    <Building2 className="w-5 h-5 text-zinc-400" />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 justify-between">
                        <div className="font-display text-lg truncate">{c.company_name}</div>
                        {c.is_archived && (
                            <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm border border-amber-500/40 text-amber-300 shrink-0">
                                Archived
                            </span>
                        )}
                    </div>
                    <div className="text-xs text-zinc-500 font-mono uppercase tracking-wider mt-0.5">
                        {c.industry || "—"}
                    </div>
                </div>
                <button
                    type="button"
                    onClick={onAction}
                    disabled={busy}
                    data-testid={c.is_archived ? `client-unarchive-${c.id}` : `client-archive-${c.id}`}
                    title={c.is_archived ? "Restore client" : "Archive client"}
                    className="p-1 rounded-sm border border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-500 disabled:opacity-50 transition-colors shrink-0"
                >
                    {c.is_archived
                        ? <ArchiveRestore className="w-3.5 h-3.5" />
                        : <Archive className="w-3.5 h-3.5" />}
                </button>
            </div>
            <div className="mt-4 text-sm text-zinc-400 line-clamp-2">{c.brand_story || c.product_service || "No description."}</div>
            <div className="mt-4 flex items-center gap-3 text-zinc-500">
                {c.website && <Globe className="w-3.5 h-3.5" />}
                {c.instagram && <Instagram className="w-3.5 h-3.5" />}
            </div>
        </Link>
    );
}
